import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, ReactNode } from 'react';
import { gitlabService } from '../services/gitlabService';
import { GitLabProject } from '../types/gitlab';

interface GitLabUser {
  username: string;
  name: string;
  avatar_url: string;
}

interface GitLabContextType {
  isAuthenticated: boolean;
  user: GitLabUser | null;
  projects: GitLabProject[];
  isLoading: boolean;
  error: string | null;
  connect: (gitlabUrl: string, accessToken: string) => Promise<boolean>;
  disconnect: () => void;
  refreshProjects: () => Promise<void>;
}

const GitLabContext = createContext<GitLabContextType | undefined>(undefined);

export const GitLabProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<GitLabUser | null>(null);
  const [projects, setProjects] = useState<GitLabProject[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const connect = useCallback(async (gitlabUrl: string, accessToken: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      gitlabService.configure(gitlabUrl, accessToken);
      const isValid = await gitlabService.validateConnection();

      if (isValid) {
        const currentUser = await gitlabService.getCurrentUser();
        setUser(currentUser);
        setIsAuthenticated(true);

        // Store credentials in localStorage
        localStorage.setItem('gitlab_url', gitlabUrl);
        localStorage.setItem('gitlab_token', accessToken);

        // Fetch projects
        const fetchedProjects = await gitlabService.getAllProjects();
        setProjects(fetchedProjects);
        
        // Enrich projects with version info in background with progress updates
        gitlabService.enrichProjectsWithVersionInfo(fetchedProjects, (enriched) => {
          setProjects(enriched);
        });

        return true;
      } else {
        setError('Invalid credentials or connection failed');
        return false;
      }
    } catch (err: any) {
      setError(err.message || 'Connection failed');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const disconnect = useCallback(() => {
    setIsAuthenticated(false);
    setUser(null);
    setProjects([]);
    localStorage.removeItem('gitlab_url');
    localStorage.removeItem('gitlab_token');
  }, []);

  const refreshProjects = useCallback(async () => {
    if (!isAuthenticated) return;

    setIsLoading(true);
    try {
      const fetchedProjects = await gitlabService.getAllProjects();
      setProjects(fetchedProjects);
      
      // Enrich projects with version info in background with progress updates
      gitlabService.enrichProjectsWithVersionInfo(fetchedProjects, (enriched) => {
        setProjects(enriched);
      });
    } catch (err: any) {
      setError(err.message || 'Failed to fetch projects');
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // Auto-reconnect on app load if credentials exist in localStorage
  useEffect(() => {
    const savedUrl = localStorage.getItem('gitlab_url');
    const savedToken = localStorage.getItem('gitlab_token');
    
    if (savedUrl && savedToken && !isAuthenticated) {
      connect(savedUrl, savedToken);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const contextValue = useMemo(() => ({
    isAuthenticated,
    user,
    projects,
    isLoading,
    error,
    connect,
    disconnect,
    refreshProjects,
  }), [isAuthenticated, user, projects, isLoading, error, connect, disconnect, refreshProjects]);

  return (
    <GitLabContext.Provider value={contextValue}>
      {children}
    </GitLabContext.Provider>
  );
};

export const useGitLab = (): GitLabContextType => {
  const context = useContext(GitLabContext);
  if (!context) {
    throw new Error('useGitLab must be used within a GitLabProvider');
  }
  return context;
};
